import React, { useState, useEffect } from "react";
import { get } from "../../../services/api";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

function EmployeeList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await get(`Employee/list?CenterId=${savedCenterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.success) {
        setEmployees(response.data || []);
        toast.success(`Loaded ${response.data?.length || 0} employees`);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Filter employees
  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      emp.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.primaryPhone?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      emp.statusName?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Navigate to employee profile
  const handleOpenProfile = (employeeId) => {
    navigate(`/employee?mode=view&employeeId=${employeeId}`);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const data = filteredEmployees.map((emp) => ({
      "Employee Code": emp.code,
      "Full Name": emp.fullName,
      Status: emp.statusName,
      Department: emp.departmentName || "N/A",
      Designation: emp.designationName || "N/A",
      Email: emp.email,
      Phone: emp.primaryPhone,
      "Joining Date": emp.joiningDate?.split("T")[0] || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");
    XLSX.writeFile(
      workbook,
      `Employee_List_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
    toast.success("Excel exported successfully");
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape");

    const columns = [
      { header: "Code", dataKey: "code" },
      { header: "Name", dataKey: "name" },
      { header: "Status", dataKey: "status" },
      { header: "Department", dataKey: "department" },
      { header: "Designation", dataKey: "designation" },
      { header: "Email", dataKey: "email" },
      { header: "Phone", dataKey: "phone" },
    ];

    const rows = filteredEmployees.map((emp) => ({
      code: emp.code,
      name: emp.fullName,
      status: emp.statusName,
      department: emp.departmentName || "N/A",
      designation: emp.designationName || "N/A",
      email: emp.email,
      phone: emp.primaryPhone,
    }));

    doc.setFontSize(16);
    doc.text("Employee List", 14, 15);

    doc.autoTable({
      columns,
      body: rows,
      startY: 25,
      theme: "grid",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`Employee_List_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF exported successfully");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Employee List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredEmployees.length} employees found
            </Typography>
          </div>

          {/* ✅ ADD THIS BUTTON */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<i className="fas fa-plus"></i>}
            onClick={() => navigate("/employee?mode=new")}
          >
            Create New Employee
          </Button>
        </Box>
      </Box>

      {/* Filters and Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1 }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="probation">Probation</MenuItem>
              <MenuItem value="resigned">Resigned</MenuItem>
              <MenuItem value="terminated">Terminated</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search name, code, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            size="small"
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            size="small"
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Employee Cards */}
      {filteredEmployees.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary" py={5}>
              No employees found. Try adjusting your filters.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {filteredEmployees.map((employee) => (
              <Grid item xs={12} md={6} xl={4} key={employee.id}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <Avatar
                        src={employee.photoUrl}
                        sx={{
                          width: 74,
                          height: 74,
                          borderRadius: 4,
                          bgcolor: "primary.main",
                        }}
                      >
                        {employee.fullName?.charAt(0)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {employee.fullName}
                          </Typography>
                          <Chip
                            label={employee.statusName || "N/A"}
                            color={
                              employee.statusName?.toLowerCase() === "active"
                                ? "success"
                                : employee.statusName?.toLowerCase() ===
                                    "probation"
                                  ? "warning"
                                  : "default"
                            }
                            size="small"
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {employee.code} • {employee.departmentName || "N/A"}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" display="block">
                            <strong>Designation:</strong>{" "}
                            {employee.designationName || "N/A"}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>Email:</strong> {employee.email}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>Phone:</strong> {employee.primaryPhone}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenProfile(employee.id)}
                      >
                        Open Profile
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Quick Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Table View
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mt: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                      <TableCell>Code</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Designation</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEmployees.map((employee) => (
                      <TableRow key={employee.id} hover>
                        <TableCell>{employee.code}</TableCell>
                        <TableCell>{employee.fullName}</TableCell>
                        <TableCell>
                          <Chip
                            label={employee.statusName || "N/A"}
                            color={
                              employee.statusName?.toLowerCase() === "active"
                                ? "success"
                                : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {employee.departmentName || "N/A"}
                        </TableCell>
                        <TableCell>
                          {employee.designationName || "N/A"}
                        </TableCell>
                        <TableCell>{employee.email}</TableCell>
                        <TableCell>{employee.primaryPhone}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

export default EmployeeList;
